import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
  ChangeDetectionStrategy
} from '@angular/core';
import {AbstractWidget} from '../../../../../models/cms/widget/abstract-widget';
import {FormsModule} from '@angular/forms';
import {WidgetService} from '../../../../../services/cms/widget.service';

import {AbstractOptionComponent} from '../option/abstract-option/abstract-option.component';
import {TextOptionComponent} from '../option/text-option/text-option.component';
import {ChoiceOptionComponent} from '../option/choice-option/choice-option.component';
import {TextListOptionComponent} from '../option/text-list-option/text-list-option.component';
import {HtmlOptionComponent} from '../option/html-option/html-option.component';

@Component({
  selector: 'app-widget-viewer',
  imports: [
    FormsModule
  ],
  templateUrl: './widget-viewer.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './widget-viewer.component.scss'
})
export class WidgetViewerComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() widget: AbstractWidget | null = null;
  @Input() theme: any | null = null;

  @ViewChild('iframe') iframe: ElementRef | null = null;
  @ViewChild('optionsContainer', {read: ViewContainerRef, static: true}) optionsContainer!: ViewContainerRef;

  country: string = 'FR';
  widgetData: any = {};
  options: any[] = [];
  loading = false;

  constructor(
    private widgetService: WidgetService,
  ) {
  }

  ngOnInit(): void {
    const language = navigator.language.split('-');
    if (language.length === 2) {
      this.country = language[1];
    } else {
      this.country = 'FR';
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['widget']) {
      this.loadWidget();
    } else if (changes['theme']) {
      this.refresh();
    }
  }

  ngAfterViewInit() {
    const iframe = this.iframe?.nativeElement;

    iframe.addEventListener('load', () => {
      const iframeDocument = iframe.contentDocument;
      if (!iframeDocument) return;

      const observer = new ResizeObserver(entries => {
        for (const entry of entries) {
          const doc = iframe.contentDocument || iframe.contentWindow.document;
          const height = Math.max(
            doc.body.scrollHeight,
            doc.body.offsetHeight,
            doc.body.clientHeight,
            doc.documentElement.scrollHeight,
            doc.documentElement.offsetHeight,
            doc.documentElement.clientHeight
          );
          iframe.style.height = height + 'px';
        }
      });

      observer.observe(iframeDocument.body);
    });
  }

  loadWidget(): void {
    if (this.widget && this.widget.type) {
      this.loading = true;

      this.widgetService.getWidgetOptions(this.widget.type).subscribe((options) => {
        this.options = options;

        this.refreshOptions();

        this.refresh();
      });
    }
  }

  refresh(): void {
    if (this.widget) {
      this.loading = true;

      this.widgetService.request(this.widget, this.country, this.theme).subscribe(widgetData => {
        this.widgetData = widgetData;

        this.update();

        this.loading = false;
      });
    }
  }

  update(): void {
    const iframe = this.iframe?.nativeElement;
    if (iframe && this.widget && this.widget.id) {
      iframe.style.height = '0px';

      let document = '';

      document += '<html lang="en">' + "\n";
      document += '<head>' + "\n";
      document += '<meta name="viewport" content="width=device-width, initial-scale=1.0">' + "\n";
      document += '<link rel="preconnect" href="https://fonts.googleapis.com">' + "\n";
      document += '<link rel="preconnect" href="https://fonts.gstatic.com">' + "\n";

      if (this.widgetData?.styles) {
        this.widgetData.styles.forEach((url: string) => {
          document += '<link rel="stylesheet" href="' + url + '">' + "\n";
        });
      }

      document += '<style>' + "\n";
      document += 'body { margin: 0; }' + "\n";
      if (this.widgetData?.inlineStyles) {
        document += this.widgetData.inlineStyles;
      }
      document += '</style>' + "\n";

      if (this.widgetData?.scripts) {
        this.widgetData.scripts.forEach((url: string) => {
          document += '<script src="' + url + '" defer></script>' + "\n";
        });
      }

      if (this.widgetData?.scriptsData) {
        const data: any = {};
        data[this.widget.id] = this.widgetData.scriptsData;
        document += '<script>window[\'widget_data\'] = ' + JSON.stringify(data) + ';</script>';
      }

      document += '</head>' + "\n";
      document += '<body>' + "\n";

      document += '<widget widget-id="' + this.widget.id + '" class="' + this.widgetData?.identifier + '">' + "\n";

      if (this.widgetData?.inlineScripts) {
        document += '<script>' + this.widgetData.inlineScripts + '</script>' + "\n";
      }

      document += (this.widgetData?.html ?? '') + "\n";
      document += '</widget>' + "\n";
      document += '</body>' + "\n";
      document += '</html>' + "\n";

      iframe.srcdoc = document;
    }
  }

  refreshOptions(): void {
    this.optionsContainer.clear();

    this.options.forEach(option => {
      this.createOption(option);
    });
  }

  createOption(option: any): AbstractOptionComponent | null {
    let componentType: any = null;

    switch (option.type) {
      case 'text':
        componentType = TextOptionComponent;
        break;
      case 'text-list':
        componentType = TextListOptionComponent;
        break;
      case 'choice':
        componentType = ChoiceOptionComponent;
        break;
      case 'html':
        componentType = HtmlOptionComponent;
        break;
      default:
        console.error('Unknown option type: ' + option.type);
        return null;
    }

    const componentRef = this.optionsContainer.createComponent(componentType);
    const component = componentRef.instance as AbstractOptionComponent;

    component.label = option.label;
    component.property = option.property;

    component.setData(option);

    const options = this.widget?.options ?? null;
    if (options) {
      if (option.property in options) {
        const value = options[option.property];
        component.setValue(value);
      }
    }

    if (component.onValueChange) {
      component.onValueChange((property: string, value: any) => {
        if (this.widget) {
          this.widget.options[property] = value;
        }
      });
    }

    componentRef.location.nativeElement.classList.add('option-component');

    return component;
  }
}
